import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ButtonModule } from 'primeng/button';
import { getTintedContrastColor } from '@shared/utils/color-utils';
import { FixColorpickerXScrollDirective } from '@shared/directives/colorpicker.directive';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';

export interface ColorPickerConfig {
  // Dependency tracking for automatic reloading
  dependsOn?: string; // Field key that this field depends on for dependency tracking
  onDependencyChange?: (dependencyValue: any) => string | null; // Callback when dependency changes - should return the color value to set
  placeholder?: string;
  disabled?: boolean;
  format?: 'hex' | 'rgb' | 'hsb'; // Color format
  inline?: boolean; // Whether to show inline color picker
  showContrastButton?: boolean; // Whether to show the contrast color button
  contrastSourceField?: string; // Field to get contrast color from
}

@Component({
  selector: 'generic-colorpicker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ColorPickerModule,
    ButtonModule,
    TooltipModule,
    InputTextModule,
    FixColorpickerXScrollDirective
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GenericColorPickerComponent),
      multi: true
    }
  ],
  templateUrl: './generic-colorpicker.component.html',
  styleUrls: ['./generic-colorpicker.component.scss']
})
export class GenericColorPickerComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() id: string = '';
  @Input() config: ColorPickerConfig = {};
  @Input() disabled: boolean = false;
  @Input() dependencyValue: any = null; // Value that this component depends on
  @Input() contrastSourceValue: any = null; // Value from the contrast source field

  @Output() contrastColorApplied = new EventEmitter<string>();

  // ControlValueAccessor properties
  value: any = null;
  onChange = (value: any) => { };
  onTouched = () => { };

  ngOnInit() {
    // Component initialization
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle dependency changes
    if (changes['dependencyValue'] && !changes['dependencyValue'].firstChange) {
      const currentValue = changes['dependencyValue'].currentValue;
      const previousValue = changes['dependencyValue'].previousValue;

      // Only process if the value actually changed
      if (currentValue !== previousValue) {
        // Call onDependencyChange callback if available
        if (this.config.onDependencyChange) {
          // Get the new color value from the callback
          const newColorValue = this.config.onDependencyChange(currentValue);

          // Update the colorpicker value if a color was returned
          if (newColorValue !== null && newColorValue !== undefined) {
            this.value = newColorValue;
            this.onChange(newColorValue);
          } else if (newColorValue === null) {
            // Explicitly clear the color if null is returned
            this.value = null;
            this.onChange(null);
          }
        }
      }
    }
  }

  onTextInputChange(inputValue: string) {
    // Handle empty input - allow clearing the color
    if (!inputValue || inputValue.trim() === '') {
      this.value = null;
      this.onChange(null);
      return;
    }

    // Validate color format (basic hex validation)
    const trimmedValue = inputValue.trim();
    if (this.isValidColor(trimmedValue)) {
      this.value = trimmedValue;
      this.onChange(trimmedValue);
    } else {
      // For invalid colors, still update the display but don't emit the change
      // This allows users to see what they're typing
      this.value = trimmedValue;
    }
  }

  onColorPickerChange(color: string) {
    // Color picker always provides valid colors
    this.value = color;
    this.onChange(color);
    this.onTouched();
  }

  private isValidColor(color: string): boolean {
    // Basic validation for hex colors
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    // Check for hex format
    if (hexPattern.test(color)) {
      return true;
    }

    // Check for rgb format
    const rgbPattern = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;
    if (rgbPattern.test(color)) {
      const matches = color.match(rgbPattern);
      if (matches) {
        const [, r, g, b] = matches;
        return parseInt(r) <= 255 && parseInt(g) <= 255 && parseInt(b) <= 255;
      }
    }

    // Check for named colors (basic set)
    const namedColors = ['red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink', 'brown', 'black', 'white', 'gray', 'grey'];
    if (namedColors.includes(color.toLowerCase())) {
      return true;
    }

    return false;
  }

  getContrastSourceValue(): string | null {
    // Use the contrastSourceValue input instead of accessing formGroup
    return this.contrastSourceValue || null;
  }

  applyContrastColor() {
    const sourceColor = this.getContrastSourceValue();
    if (!sourceColor) return;

    try {
      const contrastColor = getTintedContrastColor(sourceColor);
      this.value = contrastColor;
      this.onChange(contrastColor);
      this.onTouched();
      this.contrastColorApplied.emit(contrastColor);
    } catch (error) {
      console.error('Error generating contrast color:', error);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Public methods
  clearColor() {
    this.value = null;
    this.onChange(null);
  }

  setColor(color: string) {
    this.value = color;
    this.onChange(color);
  }

  getColorPickerStyleClass(): string {
    const baseClass = 'color-picker-button';
    const emptyClass = 'color-preview-empty';

    if (!this.value || this.value.trim() === '') {
      return `${baseClass} ${emptyClass}`;
    }

    return baseClass;
  }
}
